import { Map as ImmutableMap } from 'immutable';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { billerAccountsActions, billerAccountsSelectors } from 'app-components/flux/biller-accounts';
// setup
import { billersActions, billersSelectors } from 'app-components/flux/billers';
import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';
import { getEnvironmentConfig, getLogger, httpClientInterface, tracker } from 'app-components/utils/core';

// local
import * as actions from './billerLoginsActions';
import { getBillerLoginForId, getLastSyncDate } from './billerLoginsSelectors';
import {
  transformBillerLoginToCreateRequestData,
  transformBillerLoginToRequestData,
  transformBillerLoginToUpdateRequestData,
  transformResponseToBillerLogins,
} from './billerLoginsTransformers';
import { RefreshStatusEnum, mkBillerLogin } from './billerLoginsTypes';
import { inError, isRefreshDone, keepLogin, mkUIBillerLogin } from './billerLoginsUtils';
import { encryptBillerLoginCredentials } from './encryption';

const log = getLogger('data/billerLogins/sagas');

// =============================================================================
// Discovery + Refresh Saga Functions
// =============================================================================

export function* loginDiscovery(action) {
  let billerLogin = action.payload.newLogin;
  const { reconnect, mixpanelProps, MFA, waitForPolling = false } = action.payload;
  // these are used to update the UI & state values in the LoginPage
  const { resolve, reject } = action.meta; // 'resolve' is treated as complete, 'reject' as error - one needs to be called to reset UI

  let resolvedBillerLogin = billerLogin;

  const { encryptedCredentials, isSecure, fieldEncryptionId } = encryptBillerLoginCredentials(billerLogin);
  if (isSecure) {
    billerLogin = billerLogin.set('isEncryptionEnabled', true);
    billerLogin = billerLogin.set('credentialAnswers', encryptedCredentials);
    billerLogin = billerLogin.set('fieldEncryptionId', fieldEncryptionId);
  } else {
    log.log('UnEncrypted payload!');
  }
  log.log('Disovering biller login:', billerLogin);

  let billerLoginId = billerLogin.id !== '0' ? billerLogin.id : null;

  let requestUrl = `${getEnvironmentConfig().services_url}/billpresentment/billerlogins`;
  if (billerLoginId) {
    requestUrl += `/${billerLoginId}`;
  }
  const requestBody = billerLoginId
    ? transformBillerLoginToUpdateRequestData(billerLogin)
    : transformBillerLoginToCreateRequestData(billerLogin);

  let loginStatus;
  const httpMethod = billerLoginId ? 'PUT' : 'POST';
  try {
    const response = yield call(httpClientInterface.send, httpMethod, requestUrl, requestBody);
    log.log('Upserted biller-login, response=', response.data);

    if (!billerLoginId) {
      billerLoginId = response.data.id;
      loginStatus = response.data.status;
      resolvedBillerLogin = resolvedBillerLogin.set('id', billerLoginId);
    }
  } catch (exceptionError) {
    log.error('Error upserting biller-login, error=', exceptionError);
    const error = exceptionError;
    if (error.response) {
      if (error.response?.status === 404) {
        // login doesn't exist on put
        // clear ID so next request is a POST
        billerLogin.id = null;
        resolvedBillerLogin = resolvedBillerLogin.set('id', null);
        if (httpMethod === 'PUT') {
          assert(billerLogin.refreshStatus !== RefreshStatusEnum.LOCKED, '404 on Locked Account - should not happen');
        }
      }
      // 412 for BillGo?
      // 403 -> recreate as a put? -> puts can 403 too though
      // TODO: create error actions / handling on QCS errors?
    }
    tracker.track(reconnect ? tracker.events.fixBillerError : tracker.events.addBillerError, {
      ...mixpanelProps,
      refresh_status: resolvedBillerLogin?.refreshStatus,
    });
    yield put(actions.discoverBillerFailure(error));
    yield call(reject, error, resolvedBillerLogin); // update UI form

    return;
  }
  try {
    if (billerLoginId !== '0' && billerLoginId) {
      // if ID exists / created successfully, setup polling
      resolvedBillerLogin = resolvedBillerLogin.set('refreshStatus', RefreshStatusEnum.REFRESHING);
      // On Simplifi mobile, we do not wait for polling. Resolve immediately if the wait flag is not set.
      if (resolve && !waitForPolling) {
        yield call(resolve, resolvedBillerLogin);
      }

      if (reconnect || !waitForPolling) {
        // if rediscovery or early exit, update refresh status in redux for Icon UI to show loading
        yield put(actions.startRediscovery(resolvedBillerLogin));
      }

      const config = resourceStoreTypes.mkQcsSyncResourceConfig({
        resourceName: 'billerLogins',
        resourceBaseUrl: `/billpresentment/billerlogins/${billerLoginId}`,
        successAction: reconnect ? actions.rediscoverBillerLogin : actions.getBillerLoginSuccess,
        failureAction: actions.getBillerLoginFailure,
        transformResponseToResources: (response) => [mkBillerLogin(response.data), loginStatus, MFA],
        pollConfig: resourceStoreTypes.mkPollConfig({
          isComplete: (response, _attempt) => {
            const login = response?.data;
            resolvedBillerLogin = mkBillerLogin(login);
            log.info('Polling billerLogin\nCurrent status: ', login?.refreshStatus);
            return isRefreshDone(login);
          },
          pause: 1500,
          attempts: 200, // 1.5 * 200 = 300sec (5 minutes)
        }),
      });
      yield call(resourceSagaUtils.qcsSyncPollResource, config, actions.discoverBiller);
    }

    log.info('resolved billerLogin', resolvedBillerLogin);
    if (inError(resolvedBillerLogin)) {
      tracker.track(reconnect ? tracker.events.fixBillerError : tracker.events.addBillerError, {
        ...mixpanelProps,
        refresh_status: resolvedBillerLogin?.refreshStatus,
      });
    } else if (resolvedBillerLogin.refreshStatus === RefreshStatusEnum.REFRESHED) {
      tracker.track(tracker.events.addBillerAuthenticated, {
        ...mixpanelProps,
        updating: loginStatus === 'ASSIGNED_TO_EXISTING',
      });
    }

    // if not valid login, set id to null so that the next credentials submit is a create and not an update for a deleted login
    let componentLogin = keepLogin(resolvedBillerLogin, loginStatus, MFA)
      ? resolvedBillerLogin
      : resolvedBillerLogin.set('id', null);
    if (componentLogin.refreshStatus === RefreshStatusEnum.REFRESHING) {
      // timed out
      componentLogin = componentLogin
        .set('refreshStatus', RefreshStatusEnum.UNKNOWN)
        .set('notice', "Request timed out. Make sure you can login on this biller's website, and then try again.");
    }
    // Resolve here if we are waiting for polling to be done. Simplifi web waits for polling.
    if (waitForPolling) {
      // Update component state login value to strip special chars that are not supported by Formik web.
      yield call(resolve, mkUIBillerLogin(componentLogin));
    }
    // Show dialog based on refresh status
    if (componentLogin.refreshStatus !== RefreshStatusEnum.REFRESHED) {
      const billerDiscoveryDialogData = rootUiTypes.mkRootUiData({
        id: uuid(),
        type: 'DIALOG_TYPE_BILLER_DISCOVERY',
        props: ImmutableMap({
          ...action.payload,
          billerLoginId: resolvedBillerLogin?.id,
          updating: loginStatus === 'ASSIGNED_TO_EXISTING',
          discoverFlow: true,
          step: 'LOGIN',
        }),
      });

      log.info('Triggering Biller Discovery dialog');
      yield put(rootUiActions.createDialog(billerDiscoveryDialogData));
    }

    if (resolvedBillerLogin.refreshStatus === RefreshStatusEnum.REFRESHED) {
      yield put(
        billerAccountsActions.getBillerAccountsForLoginID({
          loginId: resolvedBillerLogin.id,
          props: {
            ...action.payload,
            updating: loginStatus === 'ASSIGNED_TO_EXISTING',
            discoverFlow: true,
          },
        }),
      );
    }
  } catch (error) {
    tracker.track(reconnect ? tracker.events.fixBillerError : tracker.events.addBillerError, {
      ...mixpanelProps,
      refresh_status: resolvedBillerLogin?.refreshStatus,
    });
    yield call(reject, error, resolvedBillerLogin);
    yield put(actions.discoverBillerFailure(error));
  }
}

function* resetBillerLoginForModel(action) {
  const modelId = action.payload;
  const { resolve, reject } = action.meta;
  if (!modelId) {
    yield call(reject, Error('resetBillerLoginForModel: modelId not found'));
    return;
  }
  try {
    const model = yield select(scheduledTransactionsSelectors.getScheduledTransactionById, modelId);
    const billerAccount = yield select(billerAccountsSelectors.getBillerAccountForId, model?.billPresentmentAccountId);
    const billerLogin = yield select(
      getBillerLoginForId,
      billerAccount?.billerLoginID || model?.billPresentmentBillerId,
    );
    const currentAccounts = yield select(billerAccountsSelectors.getBillerAccountsForLoginId, billerLogin?.id);

    const newModel = model
      .set('providerBillerId', '')
      .set('billPresentmentBillerId', '')
      .set('billPresentmentAccountId', '');

    yield put(scheduledTransactionsActions.updateScheduledTransaction(newModel));

    // Delete biller login if no other models are connected.
    let isDeleted = false;
    const scheduledTxns = yield select(scheduledTransactionsSelectors.getScheduledTransactions);
    const accountsConnected =
      currentAccounts &&
      currentAccounts.filter((account) =>
        scheduledTxns.find((aModel) => aModel.billPresentmentAccountId === account.id),
      );
    if (!accountsConnected || accountsConnected.size === 0) {
      // Delete billerLogin
      if (billerLogin) {
        yield put(actions.deleteBillerLogin(billerLogin));
      }
      isDeleted = true;
    }

    if (resolve) {
      yield call(resolve, { newModel, isDeleted });
    }
    return;
  } catch (error) {
    yield call(reject, error);
  }
}

// =============================================================================
// Watchers
// =============================================================================

const mkResourceConfig = (successAction, failureAction) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'biller-login',
    resourceBaseUrl: '/billpresentment/billerlogins',
    getLastSyncDate,

    transformResponseToResources: transformResponseToBillerLogins,
    transformResourceToRequestData: transformBillerLoginToRequestData,

    successAction,
    failureAction,
  });

function* getBillerLoginsActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.getBillerLoginsSuccess, actions.getBillerLoginsFailure);
  yield takeEvery(actions.getBillerLogins, resourceSagaUtils.qcsSyncGetResources, resourceConfig);
}

export function* getBillerLoginSuccessActionWatcher() {
  while (true) {
    const action = yield take(actions.getBillerLoginsSuccess);
    const billers = yield select(billersSelectors.getBillersById);
    const logins = action.payload.resources;
    for (let i = 0; i < logins.length; i++) {
      const currentLogin = logins[i];
      const id = currentLogin.providerBillerID;
      if (id && !billers.has(id) && id !== '0') {
        yield put(billersActions.getBiller(id));
      }
    }
  }
}

export function* updateBillerLoginActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateBillerLoginSuccess, actions.updateBillerLoginFailure);
  yield takeEvery(actions.updateBillerLogin, resourceSagaUtils.qcsSyncUpdateResource, resourceConfig);
}

export function* deleteBillerLoginActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteBillerLoginSuccess, actions.deleteBillerLoginFailure);
  yield takeEvery(actions.deleteBillerLogin, resourceSagaUtils.qcsSyncDeleteResource, resourceConfig);
}

export function* discoverBillerActionWatcher() {
  yield takeLatest(actions.discoverBiller, loginDiscovery);
}

export function* resetBillerLoginForModelActionWatcher() {
  yield takeEvery(actions.resetBillerLoginForModel, resetBillerLoginForModel);
}

// =============================================================================
// Exports
// =============================================================================

export default [
  getBillerLoginsActionWatcher,
  updateBillerLoginActionWatcher,
  deleteBillerLoginActionWatcher,
  getBillerLoginSuccessActionWatcher,
  discoverBillerActionWatcher,
  resetBillerLoginForModelActionWatcher,
];
